/**
 * MUI theme for the Vera console — the bridge between MUI and the Vera design
 * system (see `vera-design`: "Authority · Clarity · Calm"). Anchored on Tinta
 * (deep institutional ink blue) with a coherent family of official-ink status
 * stamps, Public Sans, hairline borders over shadow, small architectural radii,
 * and tabular figures so columns of folios/IDs/counts align.
 *
 * Component screens consume this theme via MUI props (`color="primary"`,
 * `success`/`error`/`warning`, `text.secondary`, …) and the raw CSS custom
 * properties in `design-tokens.css` for bespoke layout. UI copy is es-MX; the
 * theme itself is locale-agnostic.
 */

import { createTheme } from "@mui/material/styles";
import { esES } from "@mui/material/locale";
import { brandAlpha, tenantBrandScale } from "./branding";

/* Design tokens mirrored here so the theme is self-contained (the same values
   live as CSS vars in design-tokens.css for non-MUI styling). */
const TINTA = {
  50: "#EEF2F7",
  100: "#D7E0EB",
  200: "#B2C3D7",
  300: "#859EBC",
  400: "#5A789C",
  500: "#3B5A7D",
  600: "#2A4763",
  700: "#1E364E", // primary anchor
  800: "#15273A",
  900: "#0D1A28",
};

/* Tenant branding may re-anchor the whole brand ramp (see branding.ts); the
   CSS vars (--tinta-*) are re-anchored in tandem by applyTenantBrandCssVars,
   so MUI palette and token-based styling stay in lockstep. */
const BRAND = tenantBrandScale ?? TINTA;

const NEUTRAL = {
  paper: "#FAFAF7",
  0: "#FFFFFF",
  50: "#F5F5F1",
  100: "#ECECE6",
  200: "#DDDDD6",
  300: "#C6C6BD",
  400: "#A2A299",
  500: "#79796F",
  600: "#59594F",
  700: "#3F3F37",
  800: "#292924",
  900: "#171713",
};

const STATUS = {
  aprobado: { ink: "#2C6A4B", strong: "#225338", surface: "#E7F0EA", border: "#BCD6C7" },
  rechazado: { ink: "#A6362F", strong: "#832823", surface: "#F6E8E6", border: "#E3C0BC" },
  pendiente: { ink: "#9A6B12", strong: "#7C540B", surface: "#F6EEDB", border: "#E4D3A6" },
  escalado: { ink: "#5E4A8C", strong: "#4A3A70", surface: "#EDEAF5", border: "#CFC6E4" },
};

const FONT_SANS =
  '"Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

export const theme = createTheme(
  {
    palette: {
      mode: "light",
      primary: {
        main: BRAND[700],
        light: BRAND[500],
        dark: BRAND[800],
        contrastText: NEUTRAL[0],
      },
      // Escalado — registry violet, held apart from the primary blue.
      secondary: {
        main: STATUS.escalado.ink,
        light: "#7A66A6",
        dark: STATUS.escalado.strong,
        contrastText: "#FFFFFF",
      },
      success: {
        main: STATUS.aprobado.ink,
        dark: STATUS.aprobado.strong,
        light: STATUS.aprobado.surface,
        contrastText: "#FFFFFF",
      },
      error: {
        main: STATUS.rechazado.ink,
        dark: STATUS.rechazado.strong,
        light: STATUS.rechazado.surface,
        contrastText: "#FFFFFF",
      },
      warning: {
        main: STATUS.pendiente.ink,
        dark: STATUS.pendiente.strong,
        light: STATUS.pendiente.surface,
        contrastText: "#FFFFFF",
      },
      info: {
        main: BRAND[600],
        dark: BRAND[800],
        light: BRAND[50],
        contrastText: "#FFFFFF",
      },
      background: { default: NEUTRAL.paper, paper: NEUTRAL[0] },
      text: {
        primary: NEUTRAL[900],
        secondary: NEUTRAL[600],
        disabled: NEUTRAL[400],
      },
      divider: NEUTRAL[200],
      grey: {
        50: NEUTRAL[50],
        100: NEUTRAL[100],
        200: NEUTRAL[200],
        300: NEUTRAL[300],
        400: NEUTRAL[400],
        500: NEUTRAL[500],
        600: NEUTRAL[600],
        700: NEUTRAL[700],
        800: NEUTRAL[800],
        900: NEUTRAL[900],
      },
      action: {
        hover: brandAlpha(BRAND[700], 0.04),
        selected: BRAND[50],
        focus: brandAlpha(BRAND[500], 0.12),
      },
    },
    shape: { borderRadius: 6 }, // --radius-lg; controls drop to 4 below
    typography: {
      fontFamily: FONT_SANS,
      // Tabular, lining figures everywhere by default — folios/IDs/counts align.
      allVariants: { fontVariantNumeric: "tabular-nums lining-nums" },
      h1: { fontSize: "1.75rem", fontWeight: 600, lineHeight: 1.25, letterSpacing: "-0.01em" }, // 28px
      h2: { fontSize: "1.4375rem", fontWeight: 600, lineHeight: 1.25, letterSpacing: "-0.005em" }, // 23px
      h3: { fontSize: "1.1875rem", fontWeight: 600, lineHeight: 1.3 }, // 19px
      h4: { fontSize: "1.0625rem", fontWeight: 600, lineHeight: 1.35 }, // 17px
      h5: { fontSize: "0.9375rem", fontWeight: 600 }, // 15px
      h6: { fontSize: "0.8125rem", fontWeight: 600 }, // 13px
      subtitle1: { fontSize: "0.9375rem", fontWeight: 500 },
      subtitle2: { fontSize: "0.8125rem", fontWeight: 600 },
      body1: { fontSize: "0.9375rem", lineHeight: 1.45 }, // 15px
      body2: { fontSize: "0.8125rem", lineHeight: 1.45 }, // 13px
      button: { fontWeight: 600, letterSpacing: "0.005em" },
      caption: { fontSize: "0.75rem" }, // 12px
      overline: {
        fontSize: "0.6875rem",
        fontWeight: 600,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        lineHeight: 1.4,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: NEUTRAL.paper,
            WebkitFontSmoothing: "antialiased",
            textRendering: "optimizeLegibility",
          },
          "::selection": { background: brandAlpha(BRAND[500], 0.22) },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: "default" },
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${NEUTRAL[200]}`,
            backgroundColor: NEUTRAL[0],
            color: NEUTRAL[900],
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: "none" },
          outlined: { borderColor: NEUTRAL[200] },
        },
      },
      MuiCard: {
        defaultProps: { variant: "outlined" },
        styleOverrides: {
          root: { borderColor: NEUTRAL[200], borderRadius: 6 },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 4,
            "&.MuiButton-contained.MuiButton-colorPrimary:hover": { backgroundColor: BRAND[800] },
            "&.MuiButton-contained.MuiButton-colorPrimary:active": { backgroundColor: BRAND[900] },
          },
          outlined: { borderColor: NEUTRAL[300] },
          sizeLarge: { paddingTop: 10, paddingBottom: 10, fontSize: "0.9375rem" },
        },
      },
      MuiIconButton: {
        styleOverrides: { root: { borderRadius: 4 } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 4, backgroundColor: NEUTRAL[0] },
          notchedOutline: { borderColor: NEUTRAL[300] },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 4 },
          sizeSmall: { height: 22, fontSize: "0.6875rem", letterSpacing: "0.02em" },
          outlined: { borderColor: NEUTRAL[300] },
        },
      },
      MuiTableHead: {
        styleOverrides: { root: { backgroundColor: NEUTRAL[50] } },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: NEUTRAL[200] },
          head: {
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: NEUTRAL[500],
            whiteSpace: "nowrap",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.84375rem",
            minHeight: 44,
          },
        },
      },
      MuiTabs: {
        styleOverrides: { root: { minHeight: 44 } },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { borderColor: NEUTRAL[200], backgroundColor: NEUTRAL[0] },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            "&.Mui-selected": {
              backgroundColor: BRAND[50],
              color: BRAND[700],
              "&:hover": { backgroundColor: BRAND[100] },
              "& .MuiListItemIcon-root": { color: BRAND[700] },
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            borderLeftWidth: 3,
            borderLeftStyle: "solid",
            "&.MuiAlert-standard.MuiAlert-colorError": { borderLeftColor: STATUS.rechazado.ink },
            "&.MuiAlert-standard.MuiAlert-colorWarning": { borderLeftColor: STATUS.pendiente.ink },
            "&.MuiAlert-standard.MuiAlert-colorSuccess": { borderLeftColor: STATUS.aprobado.ink },
            "&.MuiAlert-standard.MuiAlert-colorInfo": { borderLeftColor: BRAND[600] },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: NEUTRAL[800],
            fontSize: "0.75rem",
            borderRadius: 4,
          },
        },
      },
      MuiLink: {
        defaultProps: { underline: "hover" },
        styleOverrides: { root: { color: BRAND[600], fontWeight: 500 } },
      },
    },
  },
  esES,
);

/** Status/brand token maps for bespoke (non-MUI-palette) styling needs. */
export const statusTokens = STATUS;
export const tintaTokens = TINTA;
export const neutralTokens = NEUTRAL;
